import { Button } from 'antd';
import { Plus, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import type { TruckClass } from '../../../../reference-data/reference-data.api';
import { TableSkeleton } from '../../../../../shared/components/TableSkeleton';
import { CoupleTrailerDrawer } from '../../../components/CoupleTrailerDrawer';
import { tripNotice } from '../../../policies/coupling-labels';
import { CreateTrailerDrawer } from '../../trailers/components/CreateTrailerDrawer';
import { useCreateTrailer } from '../../trailers/hooks/use-create-trailer';
import { CreateTruckDrawer } from '../../trucks/components/CreateTruckDrawer';
import { useCreateTruck } from '../../trucks/hooks/use-create-truck';
import { useTruckCatalogues } from '../../trucks/hooks/use-truck-catalogues';
import { useTruckPermissions } from '../../trucks/hooks/use-truck-permissions';
import { buildUnitColumns } from '../components/build-unit-columns';
import { UnitsTable } from '../components/UnitsTable';
import { useUnitList } from '../hooks/use-unit-list';
import { useUnitRowActions } from '../hooks/use-unit-row-actions';
import { unitPath } from '../units.paths';

/** The couple drawer a row's action opened, if any. */
function UnitDialogs({ actions, onChanged }: { actions: ReturnType<typeof useUnitRowActions>; onChanged: () => void }) {
  const rig = actions.open?.rig;
  if (!rig || !actions.subject) return null;
  return <CoupleTrailerDrawer subject={actions.subject} tripNotice={tripNotice(rig.truck)} onClose={actions.close} onCoupled={onChanged} />;
}

/** Add truck and Add trailer, each opening its create drawer. */
function useCreateDrawers(onCreated: () => void) {
  const [open, setOpen] = useState<'truck' | 'trailer' | null>(null);
  const truck = useCreateTruck(onCreated);
  const trailer = useCreateTrailer(onCreated);
  const start = (kind: 'truck' | 'trailer') => {
    (kind === 'truck' ? truck : trailer).resetCreateError();
    setOpen(kind);
  };
  return { open, truck, trailer, start, close: () => setOpen(null) };
}

/** The Add truck and Add trailer drawers. */
function CreateVehicleDrawers({ drawers, truckClasses }: { drawers: ReturnType<typeof useCreateDrawers>; truckClasses: TruckClass[] }) {
  return (
    <>
      <CreateTruckDrawer
        open={drawers.open === 'truck'}
        truckClasses={truckClasses}
        error={drawers.truck.createError}
        submitting={drawers.truck.creating}
        onCreate={drawers.truck.create}
        onClose={drawers.close}
      />
      <CreateTrailerDrawer
        open={drawers.open === 'trailer'}
        error={drawers.trailer.createError}
        submitting={drawers.trailer.creating}
        onCreate={drawers.trailer.create}
        onClose={drawers.close}
      />
    </>
  );
}

/**
 * Fleet → Units (gitaumoses4/chukua#390): the office thinks of a truck and the trailer it pulls as one unit, named by the truck's
 * registration, so the fleet is one list: each truck with its trailer, and the spare trailers. A row opens the unit; coupling a
 * lone truck or a spare trailer sits on the row, and swapping or uncoupling on the unit's page.
 */
export function UnitsPage() {
  const navigate = useNavigate();
  const { state, list, isRefreshing, reload } = useUnitList();
  const { canManageTruck } = useTruckPermissions();
  const catalogues = useTruckCatalogues();
  const actions = useUnitRowActions();
  const drawers = useCreateDrawers(() => void reload());

  if (state.status === 'forbidden') return <Navigate to="/" replace />;
  const truckClasses = catalogues.status === 'loaded' ? catalogues.truckClasses : [];
  const columns = buildUnitColumns({
    list,
    canManage: canManageTruck,
    classOptions: truckClasses.map((truckClass) => ({ text: truckClass.name, value: truckClass.code })),
    onAction: actions.start,
  });

  return (
    <div className="flex flex-col gap-4">
      {canManageTruck && (
        <div className="flex flex-wrap justify-end gap-2">
          <Button icon={<Plus className="size-4" />} onClick={() => drawers.start('trailer')}>
            Add trailer
          </Button>
          <Button type="primary" icon={<Plus className="size-4" />} onClick={() => drawers.start('truck')}>
            Add truck
          </Button>
        </div>
      )}
      {state.status === 'error' && (
        <EmptyState icon={ShieldAlert} title="Couldn't load the fleet" description="Something went wrong loading the fleet. Try reloading the page." />
      )}
      {state.status === 'loading' && <TableSkeleton label="Loading units" withSearch />}
      {state.status === 'loaded' && (
        <UnitsTable
          page={state.page}
          columns={columns}
          list={list}
          isRefreshing={isRefreshing}
          onOpen={(rig) => {
            const registration = rig.truck?.registration ?? rig.trailer?.registration;
            if (registration) navigate(unitPath(registration));
          }}
        />
      )}
      <UnitDialogs actions={actions} onChanged={() => void reload()} />
      <CreateVehicleDrawers drawers={drawers} truckClasses={truckClasses} />
    </div>
  );
}
